import { useState, useEffect } from 'react';
import StockChart from '../components/StockChart';
import TimeRangeSelector from '../components/TimeRangeSelector';
import ChartHeader from '../components/ChartHeader';
import { ErrorState, SuccessNotification } from '../components/ChartCapture';
import { captureChartElement, getDateRangeString } from '../utils/chartCaptureUtils';
import { filterDataByTimeRange, filterDataByCustomRange } from '../utils/dateFilter';
import { useStockData } from '../hooks/useStockData';
import LoadingState from '../components/ChartCapture/LoadingState';

const FullscreenChartPage = ({ symbol: initialSymbol, data: allData, onFetchSymbol }) => {
  const [timeRange, setTimeRange] = useState('All');
  const [filteredData, setFilteredData] = useState([]);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [currentSymbol, setCurrentSymbol] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stockInfo, setStockInfo] = useState(null);
  const [localData, setLocalData] = useState(allData);

  const [isCapturing, setIsCapturing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { stockDataMap } = useStockData();

  useEffect(() => {
    setLocalData(allData);
  }, [allData]);

  useEffect(() => {
    if (!currentSymbol && localData && localData.length > 0) {
      const availableSymbols = [...new Set(localData.map(item => item.symbol))];

      if (availableSymbols.length > 0 && availableSymbols[0]) {
        const firstSymbol = availableSymbols[0];
        console.log(' Auto-selecting symbol:', firstSymbol);

        setCurrentSymbol(firstSymbol);
        setSearchInput(firstSymbol);
      }
    }
  }, [localData, currentSymbol]);

  useEffect(() => {
    if (!currentSymbol) return;

    const symbolData = localData.filter(item => item.symbol === currentSymbol);

    if (symbolData.length === 0) {
      console.warn(` No data for: ${currentSymbol}`);
      setFilteredData([]);
      return;
    }

    const filtered = customDateRange
      ? filterDataByCustomRange(symbolData, customDateRange.startDate, customDateRange.endDate)
      : filterDataByTimeRange(symbolData, timeRange);

    console.log(` Filtered ${filtered.length} records for ${currentSymbol}`);
    setFilteredData(filtered);

    if (stockDataMap) {
      const info = stockDataMap.get(currentSymbol);
      setStockInfo(info || null);
    }
  }, [timeRange, localData, customDateRange, currentSymbol, stockDataMap]);

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    const symbolToSearch = e.target?.value || searchInput;
    const symbolUpper = symbolToSearch.toUpperCase().trim();

    if (!symbolUpper) return;

    console.log(` Searching for: ${symbolUpper}`);

    const existingData = localData.filter(item => item.symbol === symbolUpper);

    if (existingData.length > 0) {
      console.log(` Found ${existingData.length} existing records for ${symbolUpper}`);
      setCurrentSymbol(symbolUpper);
      setError(null);
      return;
    }

    if (onFetchSymbol) {
      try {
        setIsSearching(true);
        setError(null);

        console.log(` Fetching new data for ${symbolUpper}...`);
        const newData = await onFetchSymbol(symbolUpper);

        if (newData && newData.length > 0) {
          const updatedData = [...localData, ...newData];
          setLocalData(updatedData);
          setCurrentSymbol(symbolUpper);

          console.log(` Added ${newData.length} records for ${symbolUpper}`);
          setSuccess(`Loaded data for ${symbolUpper}!`);
          setTimeout(() => setSuccess(null), 2000);
        } else {
          setError(`No data available for symbol: ${symbolUpper}`);
        }
      } catch (err) {
        console.error(' Search error:', err);
        setError(`Failed to load ${symbolUpper}: ${err.message}`);
      } finally {
        setIsSearching(false);
      }
    } else {
      setError(`No data found for symbol: ${symbolUpper}`);

      const availableSymbols = [...new Set(localData.map(item => item.symbol))];
      console.log('📋 Available symbols:', availableSymbols);
    }
  };

  const handleCapture = async () => {
    setIsCapturing(true);
    setError(null);
    setSuccess(null);

    try {
      const element = document.querySelector('.fullscreen-chart-container');
      const dateRange = getDateRangeString(filteredData);
      const descriptionName = stockInfo?.description || 'Unknown';
      const filename = `${currentSymbol}-${descriptionName}-${dateRange}.png`;

      await captureChartElement(element, filename);

      setSuccess('Screenshot saved successfully! ✓');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to capture screenshot.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleTimeRangeSelect = (range) => {
    if (typeof range === 'object' && range.startDate && range.endDate) {
      setCustomDateRange(range);
      setTimeRange('custom');
    } else {
      setCustomDateRange(null);
      setTimeRange(range);
    }
  };

  return (
    <div className="fullscreen-chart-container relative w-full bg-dark-primary flex flex-col" style={{ minHeight: '100vh' }}>
      <ErrorState error={error} onDismiss={() => setError(null)} />
      <SuccessNotification message={success} onDismiss={() => setSuccess(null)} />



      <ChartHeader
        searchInput={searchInput}
        onSearchChange={(e) => setSearchInput(e.target.value)}
        onSearchSubmit={handleSearchSubmit}
        currentSymbol={currentSymbol}
        stockInfo={stockInfo}
        isCapturing={isCapturing}
        onCapture={handleCapture}
        stockDataMap={stockDataMap}
        allData={localData}
      />

      <div className="chart-content-area flex-1 flex flex-col">
        <div className="chart-wrapper flex-1 relative overflow-hidden">
          {isSearching && (
            <div className="loading-overlay">
              <LoadingState message="Loading symbol data..." />
            </div>
          )}

          {currentSymbol ? (
            <StockChart data={filteredData} isFullscreen={true} />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-gray-400 text-xl">Search for a stock symbol</div>
            </div>
          )}
        </div>

        <div className="time-selector-wrapper border-t border-gray-700 flex-shrink-0">
          <TimeRangeSelector selected={timeRange} onSelect={handleTimeRangeSelect} />
        </div>
      </div>
    </div>
  );
};

export default FullscreenChartPage;